/** A catalog record's Usage tab (plan §10.4, §9.5): the full, paged used-by, in the groups the
 *  record tab's "Where it's used" panel shows — the same panel shape, every row instead of the
 *  first five. The activity and the itinerary records use it; their rows, words and summary
 *  are theirs.
 *
 *  - Header: the title · a search over every listed group ("Name or #id", the endpoint's
 *    `search`) · Refresh.
 *  - Then the record's summary line (lazy, the caller's: the used-by summary is read only once
 *    the panel has been on screen — D7) — not when nothing uses the record: the empty line
 *    alone says so (never "No tours use it" above "Not used anywhere").
 *  - One row per relation that holds the record, in the record's order: its label and total
 *    ("3 of 9" while a search narrows it), then its rows a page at a time
 *    (`RELATION_PAGE_SIZE`, Previous / Next — the relation drawer's pager), each a link with the
 *    facts that make an edit or a delete risky (the caller's row). A count-only relation
 *    (travellers' own trips above the backend's cap) says what it is in place of rows, and is
 *    never searched.
 *  - Empty: the record's "nothing uses it" line. Failed: what happened, and Retry — for the
 *    totals, or for one relation's page. Loading: skeleton lines on the rows' own boxes.
 *
 *  Every query is the caller's (`pageQuery`), so its keys sit under the record kind and a
 *  write to the record refreshes them. */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, IconButton, Skeleton, TextField, Tooltip, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import type { UsedByRef, UsedByTypeOption } from '@/api/usedBy';
import { SectionCard } from '@/components/ReadView';
import { RELATION_PAGE_SIZE, RelationFailure, RelationPager } from '@/features/records/relations/RelationDrawer';
import { formatRelativeTime } from '@/utils/format';

import { usageMatchText, usageTotals, type UsageGroupDef, type UsageGroupTotal } from './usageModel';

export const USAGE_TITLE = 'Where it’s used';
const LABEL_COLUMN_PX = 168;
const SEARCH_DEBOUNCE_MS = 300;

const GROUP_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: `${LABEL_COLUMN_PX}px minmax(0, 1fr)` },
  columnGap: 2,
  rowGap: 0.5,
  py: 1.25,
  px: 1,
  borderTop: 1,
  borderColor: 'divider',
  '&:first-of-type': { borderTop: 0, pt: 0 },
  fontVariantNumeric: 'tabular-nums',
} as const;
const ROWS_SX = { listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.25 } as const;
const LINE_SX = { px: 1 } as const;
const SKELETON_ROW_WIDTHS = ['72%', '56%', '64%', '48%', '60%'];

/** One page of one relation, as the caller fetches it. */
export interface UsagePageQuery<R> {
  queryKey: readonly unknown[];
  queryFn: () => Promise<{ count: number; results: R[] }>;
}

export interface UsagePanelProps<K extends string, R extends UsedByRef> {
  /** The relations the record declares, in its order. */
  groups: readonly UsageGroupDef<K>[];
  /** Each relation's total (the used-by's first page `available_types`); undefined while it
   *  loads. */
  types: readonly UsedByTypeOption[] | undefined;
  typesFailed: boolean;
  /** Retry the totals; Refresh re-runs them, every relation's page and the summary line. */
  onRefresh: () => void;
  /** One relation's page `page` (1-based) under `search` ('' = none). */
  pageQuery: (group: K, page: number, search: string) => UsagePageQuery<R>;
  /** One row: an `<li>`. */
  renderRow: (group: K, row: R) => ReactNode;
  /** The record's summary line, under the header. */
  summary?: ReactNode;
  /** What the panel says when nothing uses the record. */
  empty: string;
  /** "this activity": the failed line's words. */
  what: string;
  /** The panel's root (the caller's lazy summary waits for it to be seen). */
  panelRef?: (el: HTMLElement | null) => void;
}

export function UsagePanel<K extends string, R extends UsedByRef>({
  groups,
  types,
  typesFailed,
  onRefresh,
  pageQuery,
  renderRow,
  summary,
  empty,
  what,
  panelRef,
}: UsagePanelProps<K, R>) {
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(input.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [input]);

  const totals = useMemo(() => (types ? usageTotals(groups, types) : undefined), [groups, types]);
  const defs = useMemo(() => new Map(groups.map((g) => [g.key, g])), [groups]);
  const unused = totals !== undefined && totals.length === 0;

  let body: ReactNode;
  if (!totals && typesFailed) {
    body = (
      <Box role="alert" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ...LINE_SX, color: 'error.main' }} data-usage-error>
        <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
        <Typography variant="body2" component="span" color="text.primary">
          {`Couldn’t load where ${what} is used.`}
        </Typography>
        <Button size="small" onClick={onRefresh} sx={{ minWidth: 0, py: 0 }}>
          Retry
        </Button>
      </Box>
    );
  } else if (!totals) {
    body = (
      <Box role="status" aria-label="Loading where it’s used" data-usage-loading>
        <Box sx={GROUP_SX}>
          <Typography variant="body2" component="div">
            <Skeleton width="60%" />
          </Typography>
          <RowsSkeleton rows={3} />
        </Box>
      </Box>
    );
  } else if (unused) {
    body = (
      <Typography variant="body2" color="text.secondary" sx={LINE_SX} data-usage-empty>
        {empty}
      </Typography>
    );
  } else {
    body = (
      <Box component="dl" sx={{ m: 0 }}>
        {totals.map((t) => (
          <UsageGroup
            // A new search starts each relation on its first page.
            key={`${t.key}:${search}`}
            def={defs.get(t.key)!}
            total={t}
            search={search}
            pageQuery={pageQuery}
            renderRow={renderRow}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box component="section" aria-label={USAGE_TITLE} data-usage-panel ref={panelRef} sx={{ '& > .MuiCard-root': { mb: 0 } }}>
      <SectionCard
        title={USAGE_TITLE}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              size="small"
              label="Find"
              placeholder="Name or #id"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              sx={{ width: { xs: 160, sm: 220 } }}
              slotProps={{ htmlInput: { 'aria-label': 'Find where it’s used, by name or #id' } }}
            />
            <Tooltip title="Refresh">
              <IconButton size="small" aria-label="Refresh where it’s used" onClick={onRefresh}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        }
      >
        {summary && !unused && (
          <Box sx={{ ...LINE_SX, mb: 1.5 }} data-usage-summary>
            {summary}
          </Box>
        )}
        {body}
      </SectionCard>
    </Box>
  );
}

function UsageGroup<K extends string, R extends UsedByRef>({
  def,
  total,
  search,
  pageQuery,
  renderRow,
}: {
  def: UsageGroupDef<K>;
  total: UsageGroupTotal<K>;
  search: string;
  pageQuery: (group: K, page: number, search: string) => UsagePageQuery<R>;
  renderRow: (group: K, row: R) => ReactNode;
}) {
  const [page, setPage] = useState(1);
  const { queryKey, queryFn } = pageQuery(def.key, page, search);
  const q = useQuery({
    queryKey,
    queryFn,
    enabled: !total.countOnly,
    placeholderData: keepPreviousData,
  });
  const rows = (q.data?.results ?? []).filter((r) => r.type_key === def.key);
  const matches = search && q.data ? q.data.count : null;

  let rowsPart: ReactNode;
  if (total.countOnly) {
    rowsPart = (
      <Typography variant="body2" color="text.secondary" data-usage-unlisted>
        {def.unlisted ?? 'Counted, not listed'}
      </Typography>
    );
  } else if (q.isLoading) {
    rowsPart = (
      <Box role="status" aria-label={`Loading the ${def.noun}`}>
        <RowsSkeleton rows={Math.min(total.count, 5)} />
      </Box>
    );
  } else if (q.isError || !q.data) {
    rowsPart = <RelationFailure what={`the ${def.noun}`} onRetry={() => void q.refetch()} />;
  } else if (!rows.length) {
    rowsPart = (
      <Typography variant="body2" color="text.secondary" data-usage-no-match>
        {search ? `No ${def.noun} match “${search}”.` : `No ${def.noun} any more.`}
      </Typography>
    );
  } else {
    rowsPart = (
      <>
        <Box component="ul" aria-label={def.label} sx={ROWS_SX} aria-busy={q.isFetching || undefined}>
          {rows.map((row) => renderRow(def.key, row))}
        </Box>
        <RelationPager page={page} count={q.data.count} shown={rows.length} onPage={setPage} />
      </>
    );
  }

  return (
    <Box data-usage-group={def.key} sx={GROUP_SX}>
      <Typography component="dt" variant="body2" sx={{ fontWeight: 600, minWidth: 0 }}>
        {def.label}
        <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }} data-usage-count>
          {` · ${usageMatchText(total, total.countOnly ? null : matches)}`}
        </Box>
      </Typography>
      <Box component="dd" sx={{ m: 0, minWidth: 0, display: 'grid', gap: 0.5, alignContent: 'start' }}>
        {rowsPart}
      </Box>
    </Box>
  );
}

function RowsSkeleton({ rows }: { rows: number }) {
  return (
    <Box sx={ROWS_SX}>
      {Array.from({ length: Math.max(rows, 1) }, (_, i) => (
        <Typography key={i} variant="body2" component="div" data-usage-skeleton-row>
          <Skeleton width={SKELETON_ROW_WIDTHS[i % SKELETON_ROW_WIDTHS.length]} />
        </Typography>
      ))}
    </Box>
  );
}

/** The page size a Usage tab asks for (the relation drawer's). */
export const USAGE_PAGE_SIZE = RELATION_PAGE_SIZE;

/** The summary line under the Usage panel's header: the tours and their upcoming departures
 *  and bookings (the used-by summary, D7 — read lazily), when it was counted (the server keeps
 *  it 10 minutes), a skeleton while it loads, and a failure with Retry. */
export function UsageSummaryLine({
  text,
  computedAt,
  failed,
  onRetry,
}: {
  /** null while it loads. */
  text: string | null;
  computedAt?: string | null | undefined;
  failed: boolean;
  onRetry: () => void;
}) {
  if (text === null && failed) {
    return <RelationFailure what="the tours’ departures" onRetry={onRetry} />;
  }
  if (text === null) {
    return (
      <Typography variant="body2" component="div" role="status" aria-label="Counting the tours’ departures">
        <Skeleton width="50%" />
      </Typography>
    );
  }
  return (
    <Typography
      variant="body2"
      component="div"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', '& > svg': { fontSize: 16, color: 'text.secondary' } }}
    >
      <EventOutlinedIcon aria-hidden />
      <span>{text}</span>
      {computedAt && (
        <Typography component="span" variant="caption" color="text.secondary">
          {`counted ${formatRelativeTime(computedAt)}`}
        </Typography>
      )}
    </Typography>
  );
}
